import { LockClosedIcon } from '@heroicons/react/24/outline'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { usePartnerContext } from '../../context'
import { useFeatureGate } from '../../features/pricing/use-feature-gate'
import LockBadge from '../pricing/lock-badge'
import SEO from '../seo'
import Button from '../tailwind/Button'
import InfoCallout from '../tailwind/InfoCallout'
import VersionSwitch from '../version-switch'
import SettingsV1 from './result-reports/settings-v1'
import SettingsV2 from './result-reports/settings-v2'

const TRANSLATION_LOCATION = 'components.settings.result_reports.'

const REPORTS_FEATURE = 'CUSTOM_REPORTS'
const REPORTS_SOURCE = 'settings_result_reports'

const Header = ({ versions = [], withVersionSwitch = false }) => {
  const { t } = useTranslation()

  return (
    <>
      <h1 className='text-xl font-semibold text-gray-900'>
        {t(TRANSLATION_LOCATION + 'title')}
      </h1>
      <p className='mt-1 max-w-lg text-sm text-gray-700 lg:max-w-2xl'>
        {t(TRANSLATION_LOCATION + 'description')}
      </p>
      {withVersionSwitch && (
        <div className='relative mt-4'>
          <div
            aria-hidden='true'
            className='absolute inset-0 flex items-center'
          >
            <div className='w-full border-t border-gray-300' />
          </div>
          <div className='relative flex justify-center'>
            <div className='bg-white px-2'>
              <VersionSwitch versions={versions} />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

Header.propTypes = {
  versions: PropTypes.array,
  withVersionSwitch: PropTypes.bool
}

const Container = ({ children = null }) => {
  return (
    <div className='bg-gray-50 px-4 py-5 sm:rounded-md sm:p-6'>{children}</div>
  )
}

Container.propTypes = { children: PropTypes.node }

// Ergebnisreports in enforced ohne CUSTOM_REPORTS. Ohne gespeicherte
// Einstellungen öffnet „Einstellungen bearbeiten" den Upgrade-Dialog, die
// Pille daneben ebenso. Gespeicherte Einstellungen bleiben aktiv und werden
// read-only gezeigt (children), Zurücksetzen geht immer.
const LockedReportSettings = ({ hasSettings, onUnlock, children }) => {
  const { t } = useTranslation()

  if (!hasSettings) {
    return (
      <div className='flex flex-wrap items-center gap-3'>
        <Button.PrimaryLG
          text={t(TRANSLATION_LOCATION + 'lockedScreen.button')}
          onClick={onUnlock}
        />
        <LockBadge
          feature={REPORTS_FEATURE}
          source={REPORTS_SOURCE}
          size='sm'
        />
      </div>
    )
  }

  return (
    <>
      <InfoCallout
        tone='neutral'
        icon={LockClosedIcon}
        className='mb-4'
        action={
          <LockBadge feature={REPORTS_FEATURE} source={REPORTS_SOURCE} inUse />
        }
      >
        {t('pricing.gates.reports_read_only_hint')}
      </InfoCallout>
      {children}
    </>
  )
}

LockedReportSettings.propTypes = {
  hasSettings: PropTypes.bool.isRequired,
  onUnlock: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired
}

const ResultReports = ({ careers }) => {
  const { t } = useTranslation()
  const { partner } = usePartnerContext()

  const hasV1Settings = !!Object.keys(
    JSON.parse(partner.app_settings?.report_v1_settings ?? '{}')
  ).length

  const hasV2Settings = !!Object.keys(
    JSON.parse(partner.app_settings?.report_v2_settings ?? '{}')
  ).length

  const hasV1Careers = careers.some(
    ({ diagnostic_version }) => diagnostic_version === 1
  )

  const hasV2Careers = careers.some(
    ({ diagnostic_version }) => diagnostic_version === 2
  )

  const [activeVersion, setActiveVersion] = useState(() => {
    if (hasV2Careers && !hasV1Careers) return 2
    if (!hasV2Careers && hasV1Careers) return 1
    if (hasV1Careers && hasV1Settings) return 1

    return 2
  })

  const [displayV1Settings, setDisplayV1Settings] = useState(hasV1Settings)
  const [displayV2Settings, setDisplayV2Settings] = useState(hasV2Settings)

  // locked nur in enforced. In Nutzung heißt, die gewählte Version hat
  // gespeicherte Einstellungen.
  const hasActiveSettings = activeVersion === 1 ? hasV1Settings : hasV2Settings
  const gate = useFeatureGate(REPORTS_FEATURE, {
    source: REPORTS_SOURCE,
    inUse: hasActiveSettings
  })

  const versions = [
    {
      version: 1,
      active: activeVersion === 1,
      handleChange: () => setActiveVersion(1)
    },
    {
      version: 2,
      active: activeVersion === 2,
      handleChange: () => setActiveVersion(2)
    }
  ]

  return (
    <>
      <SEO title={t('seo.titles.settings_resultreports')} />
      <Header
        {...{ versions, withVersionSwitch: hasV1Careers && hasV2Careers }}
      />
      <div className='mt-4' />
      {activeVersion === 1 && !gate.locked && (
        <>
          {displayV1Settings ? (
            <Container>
              <SettingsV1 />
            </Container>
          ) : (
            <Button.PrimaryLG
              text={t(TRANSLATION_LOCATION + 'lockedScreen.button')}
              onClick={() => setDisplayV1Settings(true)}
            />
          )}
        </>
      )}
      {activeVersion === 2 && !gate.locked && (
        <>
          {displayV2Settings ? (
            <SettingsV2 />
          ) : (
            <Button.PrimaryLG
              text={t(TRANSLATION_LOCATION + 'lockedScreen.button')}
              onClick={() => setDisplayV2Settings(true)}
            />
          )}
        </>
      )}
      {gate.locked && (
        <LockedReportSettings
          hasSettings={hasActiveSettings}
          onUnlock={gate.open}
        >
          {activeVersion === 1 ? (
            <Container>
              <SettingsV1 readOnly />
            </Container>
          ) : (
            <SettingsV2 readOnly />
          )}
        </LockedReportSettings>
      )}
    </>
  )
}

ResultReports.propTypes = {
  careers: PropTypes.array.isRequired
}

export default ResultReports
